/**
 * Cash-event audit ledger: deterministic within-month order, actor buckets,
 * and running “Position after this event” balances.
 *
 * Display-only enrichment — does not change buildLedger / P&L math.
 * Timing schedules still consume pre-audit events.
 */

import { MODES } from './defaults.js';
import { n } from './math.js';

/** Within-month phases (exact product order). */
export const EVENT_PHASE = {
  FUNDING: 1,
  OPERATIONS: 2,
  REPAYMENT: 3,
};

const FUNDING_KIND_RANK = {
  sponsor_funding: 1,
  capital_call: 2,
  bank_draw: 3,
  partner_draw: 4,
};

const REPAYMENT_KIND_RANK = {
  bank_repayment: 1,
  partner_repayment: 2,
  sponsor_distribution: 3,
};

const OPERATIONS_KIND_RANK = {
  land_deposit: 1,
  land_closing: 2,
  acquisition: 3,
  feasibility: 4,
  soft: 5,
  construction_tranche: 6,
  commercial: 7,
  interest: 8,
  tax: 9,
  sale: 10,
  sale_spv_cash: 11,
  sale_land_settlement: 12,
  sale_buyer_construction: 13,
};

export function eventPhase(ev) {
  const kind = ev?.kind;
  if (FUNDING_KIND_RANK[kind] != null) return EVENT_PHASE.FUNDING;
  if (REPAYMENT_KIND_RANK[kind] != null) return EVENT_PHASE.REPAYMENT;
  return EVENT_PHASE.OPERATIONS;
}

function kindRank(ev) {
  const kind = ev?.kind;
  const phase = eventPhase(ev);
  if (phase === EVENT_PHASE.FUNDING) return FUNDING_KIND_RANK[kind] || 50;
  if (phase === EVENT_PHASE.REPAYMENT) return REPAYMENT_KIND_RANK[kind] || 50;
  return OPERATIONS_KIND_RANK[kind] || 50;
}

/** Deterministic sort: month → phase → kind → id. */
export function compareCashEvents(a, b) {
  const monthDelta = (Number(a?.month) || 0) - (Number(b?.month) || 0);
  if (monthDelta) return monthDelta;
  const phaseDelta = eventPhase(a) - eventPhase(b);
  if (phaseDelta) return phaseDelta;
  const kindDelta = kindRank(a) - kindRank(b);
  if (kindDelta) return kindDelta;
  return String(a?.id || '').localeCompare(String(b?.id || ''));
}

export function sortCashEvents(events) {
  return [...(events || [])].sort(compareCashEvents);
}

/**
 * Map an outflow (or buyer-direct settlement) to a received-actor bucket.
 * Every cost maps to one bucket; land_other is the explicit fallback.
 */
export function receivedActorBucket(ev) {
  if (!ev) return 'land_other';
  const kind = ev.kind;
  const recipient = ev.recipient;
  const category = ev.category;

  if (kind === 'sale_buyer_construction' || recipient === 'contractor') return 'construction';
  if (
    kind === 'feasibility'
    || recipient === 'consultants'
    || recipient === 'professionals'
    || category === 'feasibilityCosts'
    || category === 'professionalSoftCosts'
  ) {
    return 'consultants';
  }
  if (
    kind === 'commercial'
    || recipient === 'commercial'
    || category === 'commercialCosts'
  ) {
    return 'brokers';
  }
  if (
    kind === 'land_deposit'
    || kind === 'land_closing'
    || kind === 'acquisition'
    || recipient === 'land_seller'
    || recipient === 'notary'
    || category === 'landPrice'
    || category === 'acquisitionCosts'
  ) {
    return 'land_other';
  }
  // Interest, tax, unknown recipients → explicit land/other fallback (never drop).
  return 'land_other';
}

function emptyPosition() {
  return {
    spvCash: 0,
    bankDebt: 0,
    buyerPaid: 0,
    constructionReceived: 0,
    consultantsReceived: 0,
    brokersReceived: 0,
    landOtherReceived: 0,
  };
}

function snapshotPosition(state) {
  return { ...state };
}

function creditReceived(state, bucket, amount) {
  const amt = Math.max(0, n(amount));
  if (bucket === 'construction') state.constructionReceived += amt;
  else if (bucket === 'consultants') state.consultantsReceived += amt;
  else if (bucket === 'brokers') state.brokersReceived += amt;
  else state.landOtherReceived += amt;
}

/**
 * Apply one cash event to running audit state (mutates state).
 * Quote-part buyer→contractor / buyer→bank never touch SPV cash.
 */
export function applyCashEventToPosition(state, ev) {
  const amount = n(ev?.amount);
  if (!ev || ev.meta?.nonCash || Math.abs(amount) <= 1e-12) {
    return snapshotPosition(state);
  }

  const kind = ev.kind;
  const payer = ev.payer;
  const recipient = ev.recipient;
  const direction = ev.direction;

  if (kind === 'sponsor_funding' || kind === 'capital_call') {
    state.spvCash += amount;
  } else if (kind === 'bank_draw') {
    state.spvCash += amount;
    state.bankDebt += amount;
  } else if (kind === 'partner_draw') {
    state.spvCash += amount;
  } else if (kind === 'bank_repayment') {
    state.spvCash -= amount;
    state.bankDebt = Math.max(0, state.bankDebt - amount);
  } else if (kind === 'partner_repayment') {
    state.spvCash -= amount;
  } else if (kind === 'sponsor_distribution') {
    state.spvCash -= amount;
  } else if (kind === 'sale_land_settlement') {
    // Direct buyer → bank: debt down, buyer paid up, SPV untouched.
    state.buyerPaid += amount;
    state.bankDebt = Math.max(0, state.bankDebt - amount);
  } else if (kind === 'sale_buyer_construction') {
    // Direct buyer → contractor: never SPV cash.
    state.buyerPaid += amount;
    creditReceived(state, 'construction', amount);
  } else if (kind === 'sale_spv_cash') {
    state.buyerPaid += amount;
    state.spvCash += amount;
  } else if (kind === 'sale' && !ev.meta?.nonCash) {
    // Vente à terme (or unexpanded) gross sale into SPV.
    if (payer === 'buyer' && recipient === 'spv') {
      state.buyerPaid += amount;
      state.spvCash += amount;
    }
  } else if (direction === 'out' && payer === 'spv') {
    state.spvCash -= amount;
    creditReceived(state, receivedActorBucket(ev), amount);
  } else if (direction === 'out') {
    // Non-SPV payer outflow still credits a received actor (never drop).
    creditReceived(state, receivedActorBucket(ev), amount);
    if (payer === 'buyer') state.buyerPaid += amount;
  } else if (direction === 'in' && payer === 'buyer' && recipient === 'spv') {
    state.buyerPaid += amount;
    state.spvCash += amount;
  }

  return snapshotPosition(state);
}

function pushSynthetic(events, partial) {
  const amount = n(partial.amount);
  if (Math.abs(amount) <= 1e-12) return;
  events.push({
    id: partial.id,
    month: partial.month,
    kind: partial.kind,
    label: partial.label,
    amount,
    direction: partial.direction,
    payer: partial.payer,
    recipient: partial.recipient,
    source: partial.source || 'ledger',
    recurring: false,
    category: null,
    meta: partial.meta || {},
  });
}

/**
 * Inject ledger-derived bank/partner draws & repayments into the audit stream.
 * Scale QP SPV construction-tranche amounts to the unsold share actually paid by SPV.
 */
export function buildAuditCashEventStream(displayEvents, ledger) {
  const mode = ledger?.mode;
  const months = ledger?.months || [];
  const byMonth = new Map(months.map((m) => [m.month, m]));
  const out = [];

  for (const ev of displayEvents || []) {
    if (
      mode === MODES.quotePart
      && ev.kind === 'construction_tranche'
      && ev.category === 'constructionCost'
    ) {
      const row = byMonth.get(ev.month);
      const sold = Math.min(1, Math.max(0, n(row?.salesPct)));
      const spvShare = Math.max(0, 1 - sold);
      out.push({
        ...ev,
        amount: n(ev.amount) * spvShare,
        meta: {
          ...(ev.meta || {}),
          qpSpvConstructionShare: spvShare,
          grossConstructionAmount: n(ev.amount),
        },
      });
      continue;
    }
    out.push({ ...ev, meta: { ...(ev.meta || {}) } });
  }

  for (const row of months) {
    const month = row.month;
    const bankDraw = Math.max(0, n(row.securedBankDraw));
    const partnerDraw = Math.max(0, n(row.securedPartnerDraw));
    pushSynthetic(out, {
      id: `bank-draw-${month}`,
      month,
      kind: 'bank_draw',
      label: 'Bank facility draw',
      amount: bankDraw,
      direction: 'in',
      payer: 'bank',
      recipient: 'spv',
      meta: {
        facility: 'secured',
        landFacilityDraw: Math.max(0, n(row.landFacilityDraw)),
        constructionFacilityDraw: Math.max(0, n(row.constructionFacilityDraw)),
      },
    });
    pushSynthetic(out, {
      id: `partner-draw-${month}`,
      month,
      kind: 'partner_draw',
      label: 'Partner draw',
      amount: partnerDraw,
      direction: 'in',
      payer: 'partner',
      recipient: 'spv',
      meta: {},
    });
    pushSynthetic(out, {
      id: `bank-repayment-${month}`,
      month,
      kind: 'bank_repayment',
      label: 'Bank debt repayment',
      amount: Math.max(0, n(row.bankRepayment)),
      direction: 'out',
      payer: 'spv',
      recipient: 'bank',
      meta: { repayment: true },
    });
    pushSynthetic(out, {
      id: `partner-repayment-${month}`,
      month,
      kind: 'partner_repayment',
      label: 'Partner debt repayment',
      amount: Math.max(0, n(row.partnerRepayment)),
      direction: 'out',
      payer: 'spv',
      recipient: 'partner',
      meta: { repayment: true },
    });
    pushSynthetic(out, {
      id: `sponsor-distribution-${month}`,
      month,
      kind: 'sponsor_distribution',
      label: 'Sponsor distribution',
      amount: Math.max(0, n(row.sponsorDistribution)),
      direction: 'out',
      payer: 'spv',
      recipient: 'sponsor',
      meta: { distribution: true },
    });
  }

  // Attach bank conditions / funding gap onto a representative event each month.
  const sorted = sortCashEvents(out);
  const monthFirstFunding = new Map();
  const monthLast = new Map();
  for (const ev of sorted) {
    if (!monthFirstFunding.has(ev.month) && eventPhase(ev) === EVENT_PHASE.FUNDING) {
      monthFirstFunding.set(ev.month, ev);
    }
    monthLast.set(ev.month, ev);
  }
  for (const row of months) {
    const conditions = row.unmetBankConditions || [];
    const gap = Math.max(0, n(row.fundingGap));
    if (!conditions.length && gap <= 1e-12) continue;
    const target = monthFirstFunding.get(row.month) || monthLast.get(row.month);
    if (!target) continue;
    target.meta = {
      ...(target.meta || {}),
      unmetBankConditions: conditions,
      fundingGap: gap,
    };
  }

  return sorted;
}

/**
 * Walk the audit stream and attach positionAfter to each event.
 */
export function attachPositionsAfterEvents(events) {
  const state = emptyPosition();
  return (events || []).map((ev) => {
    const positionAfter = applyCashEventToPosition(state, ev);
    return {
      ...ev,
      positionAfter,
    };
  });
}

/**
 * Full display audit pipeline: QP display events → inject ledger flows → order → positions.
 */
export function materializeCashEventAudit(displayEvents, ledger) {
  if (!ledger || ledger.incomplete) {
    return attachPositionsAfterEvents(sortCashEvents(displayEvents || []));
  }
  const stream = buildAuditCashEventStream(displayEvents, ledger);
  return attachPositionsAfterEvents(stream);
}

/**
 * Conservation helper for tests: every SPV operational outflow credits exactly one actor bucket.
 */
export function actorFlowTotals(events) {
  const totals = {
    spvOperationalOut: 0,
    buyerDirectConstruction: 0,
    constructionReceived: 0,
    consultantsReceived: 0,
    brokersReceived: 0,
    landOtherReceived: 0,
    buyerPaid: 0,
  };
  let prev = emptyPosition();
  for (const ev of events || []) {
    const pos = ev.positionAfter || emptyPosition();
    totals.constructionReceived = pos.constructionReceived;
    totals.consultantsReceived = pos.consultantsReceived;
    totals.brokersReceived = pos.brokersReceived;
    totals.landOtherReceived = pos.landOtherReceived;
    totals.buyerPaid = pos.buyerPaid;

    if (ev.meta?.nonCash) {
      prev = pos;
      continue;
    }
    const amount = n(ev.amount);
    if (
      ev.direction === 'out'
      && ev.payer === 'spv'
      && !ev.meta?.repayment
      && !ev.meta?.distribution
      && ev.kind !== 'bank_repayment'
      && ev.kind !== 'partner_repayment'
      && ev.kind !== 'sponsor_distribution'
    ) {
      totals.spvOperationalOut += amount;
    }
    if (ev.kind === 'sale_buyer_construction') {
      totals.buyerDirectConstruction += amount;
    }
    prev = pos;
  }
  const receivedSum =
    totals.constructionReceived
    + totals.consultantsReceived
    + totals.brokersReceived
    + totals.landOtherReceived;
  return {
    ...totals,
    receivedSum,
    // SPV ops outs + buyer→contractor should equal all actor receipts
    // (land settlement buyer→bank is debt, not an actor “received” cost).
    conserves:
      Math.abs(totals.spvOperationalOut + totals.buyerDirectConstruction - receivedSum) <= 0.05,
  };
}
